import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, ArrowRight, Wand2, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export function EnhancementMorphSection() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <section className="relative py-16 lg:py-28 border-t border-border overflow-hidden bg-gradient-to-b from-background via-primary/5 to-background">
      {/* Ambient dot-grid texture for depth */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at center, hsl(var(--primary)) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="container max-w-6xl mx-auto px-4 relative">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Badge variant="outline" className="mb-4">
            {/* Pulsing "live" dot */}
            <span className="relative flex h-2 w-2 mr-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
            </span>
            Live AI Demo
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4 tracking-tight">
            Ordinary Photos to <span className="text-gradient">Premium</span> — in Seconds
          </h2>
          <p className="text-muted-foreground text-lg">
            No editing skills, no waiting on a photographer. Watch the exact AI enhancement your
            buyers will see on every listing — generated live, right here.
          </p>
        </div>

        {/* Showcase frame — framed like a real product surface */}
        <div className="relative mx-auto max-w-4xl">
          <div className="absolute -inset-8 bg-gradient-to-tr from-primary/25 via-accent/10 to-transparent blur-3xl rounded-[3rem] opacity-70 pointer-events-none" />

          <div className="relative rounded-2xl lg:rounded-[1.75rem] overflow-hidden shadow-elevated border border-border/60 bg-card ring-1 ring-foreground/5">
            {/* App chrome */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border/60 bg-muted/40">
              <span className="w-3 h-3 rounded-full bg-red-400/80" />
              <span className="w-3 h-3 rounded-full bg-amber-400/80" />
              <span className="w-3 h-3 rounded-full bg-green-400/80" />
              <div className="mx-auto flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Wand2 className="w-3.5 h-3.5 text-primary" />
                AI Photo Enhancement
              </div>
            </div>

            <video
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="block w-full aspect-video"
            >
              <source src="/enhancement-morph-v3.mp4" type="video/mp4" />
            </video>
          </div>

          {/* Floating result chips — dip below the frame for depth */}
          <div className="hidden sm:flex absolute -bottom-5 left-4 lg:left-8 items-center gap-2 rounded-full bg-card/95 backdrop-blur px-4 py-2 shadow-lg border border-border/60">
            <Check className="w-4 h-4 text-primary shrink-0" />
            <span className="text-sm font-medium">Sky replaced · Decluttered</span>
          </div>
          <div className="hidden sm:flex absolute -bottom-5 right-4 lg:right-8 items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 shadow-lg">
            <Sparkles className="w-4 h-4 shrink-0" />
            <span className="text-sm font-semibold">Relaunch-ready in minutes</span>
          </div>
        </div>

        <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button
            size="lg"
            onClick={() => navigate(user ? '/?create=1' : '/auth')}
            className="group"
          >
            {user ? 'Create your listing' : 'Start free — no card required'}
            <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
          </Button>
          <p className="text-sm text-muted-foreground">
            From $15 · One-time payment · Ready in ~10 minutes
          </p>
        </div>
      </div>
    </section>
  );
}
